// CARO. Casa de estilo — minimal, dependency-free Markdown for editorial bodies.
// Shared layer (no DOM/Worker globals): the client renders the blocks with React
// (components/Markdown.tsx) and the Worker prerenders them as escaped HTML
// (worker/lib/prerender.ts), so both read the SAME grammar:
// `##`/`###` headings, blank-line-separated paragraphs (soft-wrapped lines join
// with a space), `- ` bullet lists, inline `**bold**`, inline INTERNAL links
// `[text](/logical-path)` and figures (a block that is only
// `![alt](/media/… "caption")`). Everything else is plain text: an external or
// protocol-relative link target, or an image outside /media/, stays as typed.

import { srcSetFor } from '@/lib/images';

/**
 * An inline run of text, bold or not. `href` (links only) is a LOGICAL internal
 * path (`/estudio-de-estilo/colorimetria`, `/caro/servicios`): each renderer localizes it
 * (lp() on the client, localizedPath in the Worker).
 */
export interface MdSegment {
  text: string;
  bold: boolean;
  href?: string;
}

export type MdBlock =
  | { type: 'h2' | 'h3' | 'p'; segments: MdSegment[] }
  | { type: 'ul'; items: MdSegment[][] }
  | { type: 'figure'; src: string; alt: string; caption: string };

/**
 * An internal PAGE path: one leading slash (not `//host` nor `/\host`), no spaces or
 * parentheses, and not a file or API route (`/media/`, `/api/`), which the SPA router can't open.
 */
const INTERNAL_PATH = /^\/(?![/\\])(?!(?:media|api)(?:\/|$))[^\s()]*$/;
/** `[text](target)` as one token; the target must pass INTERNAL_PATH to become a link. */
const LINK_TOKEN = /(\[[^\]]+\]\([^)\s]+\))/g;
const LINK_PARTS = /^\[([^\]]+)\]\(([^)\s]+)\)$/;
/** A whole block that is one figure: `![alt](/media/… "caption")`. */
const FIGURE = /^!\[([^\]]*)\]\((\/media\/[^\s()"]+)(?:\s+"([^"]*)")?\)$/;

function parseBold(text: string): MdSegment[] {
  return text
    .split(/(\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((part) =>
      part.startsWith('**') && part.endsWith('**')
        ? { text: part.slice(2, -2), bold: true }
        : { text: part, bold: false },
    );
}

/**
 * Split on **bold** spans, then on internal [links](/path) inside each span (a link
 * keeps its span's boldness), keeping the delimiters out of the result.
 */
export function parseInline(text: string): MdSegment[] {
  return parseBold(text).flatMap(({ text: run, bold }) =>
    run
      .split(LINK_TOKEN)
      .filter(Boolean)
      .map((part): MdSegment => {
        const m = LINK_PARTS.exec(part);
        return m?.[1] && m[2] && INTERNAL_PATH.test(m[2]) ? { text: m[1], bold, href: m[2] } : { text: part, bold };
      }),
  );
}

/** Parse a Markdown source into blocks (same grammar as the React renderer). */
export function parseMarkdown(source: string): MdBlock[] {
  return source
    .trim()
    .split(/\n{2,}/)
    .map((block): MdBlock => {
      const lines = block.split('\n');
      const fig = FIGURE.exec(block);
      if (fig) return { type: 'figure', alt: fig[1] ?? '', src: fig[2] ?? '', caption: fig[3] ?? '' };
      if (block.startsWith('### ')) return { type: 'h3', segments: parseInline(block.slice(4)) };
      if (block.startsWith('## ')) return { type: 'h2', segments: parseInline(block.slice(3)) };
      if (lines.every((l) => l.startsWith('- '))) {
        return { type: 'ul', items: lines.map((l) => parseInline(l.slice(2))) };
      }
      // Paragraph — join soft-wrapped lines with a space.
      return { type: 'p', segments: parseInline(lines.join(' ')) };
    });
}

/** Escape a string for an HTML text node or a double/single-quoted attribute. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Maps a link's logical path to the href to publish (the Worker passes localizedPath for the page's locale). */
export type HrefFor = (logicalPath: string) => string;

function inlineHtml(segments: MdSegment[], hrefFor: HrefFor): string {
  return segments
    .map((s) => {
      if (s.href) {
        const a = `<a href="${escapeHtml(hrefFor(s.href))}">${escapeHtml(s.text)}</a>`;
        return s.bold ? `<strong>${a}</strong>` : a;
      }
      return s.bold ? `<strong>${escapeHtml(s.text)}</strong>` : escapeHtml(s.text);
    })
    .join('');
}

function figureHtml(b: { src: string; alt: string; caption: string }): string {
  const srcset = srcSetFor(b.src);
  return (
    `<figure><img src="${escapeHtml(b.src)}"` +
    (srcset ? ` srcset="${escapeHtml(srcset)}"` : '') +
    ` alt="${escapeHtml(b.alt)}" loading="lazy">` +
    (b.caption ? `<figcaption>${escapeHtml(b.caption)}</figcaption>` : '') +
    `</figure>`
  );
}

/** Render a Markdown source as HTML; every text value is escaped. Links get `hrefFor(path)` (default: as written). */
export function markdownToHtml(source: string, hrefFor: HrefFor = (p) => p): string {
  return parseMarkdown(source)
    .map((b) => {
      if (b.type === 'figure') return figureHtml(b);
      if (b.type === 'ul') return `<ul>${b.items.map((item) => `<li>${inlineHtml(item, hrefFor)}</li>`).join('')}</ul>`;
      return `<${b.type}>${inlineHtml(b.segments, hrefFor)}</${b.type}>`;
    })
    .join('');
}
